import React from "react";

import {
  CollectionMosaicDestinationFragment,
  CollectionMosaicDiningAreaCategoryFragment,
  CollectionMosaicExclusiveOfferPerPromoFragment,
  CollectionMosaicShipFragment,
  CollectionMosaicSpecialVoyageFragment,
  CollectionMosaicSuiteCategoryFragment,
  PageBuilderCollectionMosaicFragment
} from "types/graphql-types";
import PageBuilderSectionTitle from "./PageBuilderSectionTitle";
import { graphql } from "gatsby";
import {
  MosaicLayoutVariantType,
  BackgroundColor
} from "@silversea-ssc/ui-components-react";
import DestinationsMosaic from "../CollectionMosaic/DestinationsMosaic";
import DiningAreaCategoriesMosaic from "../CollectionMosaic/DiningAreaCategoriesMosaic";
import GrandVoyagesMosaic from "../CollectionMosaic/GrandVoyagesMosaic";
import ShipsMosaic from "../CollectionMosaic/ShipsMosaic";
import SuiteCategoriesMosaic from "../CollectionMosaic/SuiteCategoriesMosaic";
import WorldCruisesMosaic from "../CollectionMosaic/WorldCruisesMosaic";
import OffersMosaic from "../CollectionMosaic/OffersCollectionMosaic";
import { cardColorForBackground } from "./pageBuilderRenderHelper";
import { FilterByType } from "components/CollectionSlider/utils";

export type PageBuilderCollectionMosaicProps =
  PageBuilderCollectionMosaicFragment & {
    backgroundColor: BackgroundColor;
    collectionData: PageBuilderCollectionMosaicFragment["collectionData"];
  };

const PageBuilderCollectionMosaic: React.FC<
  React.PropsWithChildren<PageBuilderCollectionMosaicProps>
> = ({
  heading,
  collection,
  mosaicVariant,
  filterByType,
  backgroundColor,
  collectionData
}) => {
  const cardBackgroundColor = cardColorForBackground(backgroundColor);
  const mosaicColumnVariant = (mosaicVariant ||
    "column3") as MosaicLayoutVariantType;

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />

      {collection == "world-cruises" && (
        <WorldCruisesMosaic
          variant={mosaicColumnVariant}
          data={collectionData as CollectionMosaicSpecialVoyageFragment[]}
        />
      )}
      {collection == "grand-voyages" && (
        <GrandVoyagesMosaic
          variant={mosaicColumnVariant}
          data={collectionData as CollectionMosaicSpecialVoyageFragment[]}
        />
      )}
      {collection == "offers" && (
        <OffersMosaic
          variant={mosaicColumnVariant}
          offersVisibility="hideOffersCollection"
          cardBackgroundColor={cardBackgroundColor}
          data={
            collectionData as CollectionMosaicExclusiveOfferPerPromoFragment[]
          }
        />
      )}
      {collection == "destinations" && (
        <DestinationsMosaic
          variant={mosaicColumnVariant}
          filterByType={filterByType as FilterByType}
          data={collectionData as CollectionMosaicDestinationFragment[]}
        />
      )}
      {collection == "ships" && (
        <ShipsMosaic
          variant={mosaicColumnVariant}
          filterByType={filterByType as FilterByType}
          data={collectionData as CollectionMosaicShipFragment[]}
        />
      )}
      {collection == "suite-categories" && (
        <SuiteCategoriesMosaic
          variant={mosaicColumnVariant}
          data={collectionData as CollectionMosaicSuiteCategoryFragment[]}
        />
      )}
      {collection == "dining-area-categories" && (
        <DiningAreaCategoriesMosaic
          variant={mosaicColumnVariant}
          data={collectionData as CollectionMosaicDiningAreaCategoryFragment[]}
          dataContentName="dining_area_categories_mosaic"
        />
      )}
      {collection == "public-area-categories" && (
        <DiningAreaCategoriesMosaic
          variant={mosaicColumnVariant}
          data={collectionData as CollectionMosaicDiningAreaCategoryFragment[]}
          dataContentName="public_area_categories_mosaic"
        />
      )}
    </React.Fragment>
  );
};

export default PageBuilderCollectionMosaic;

export const fragments = graphql`
  fragment CollectionMosaicDestination on SanityDestination {
    _id
    id
    destinationWebCode
    isExpeditionDestination
    card {
      ...AsCard
    }
    fullPath
    metadata {
      tracking {
        name
      }
    }
  }

  fragment CollectionMosaicShip on SanityShip {
    _id
    id
    shipType
    fullPath
    metadata {
      tracking {
        name
      }
    }
    card {
      ...AsCard
    }
  }

  fragment CollectionMosaicSpecialVoyage on SanitySpecialVoyage {
    _id
    id
    isVisible
    fullPath
    metadata {
      tracking {
        name
      }
    }
    card {
      ...AsCard
    }
    data {
      comboType
    }
  }
  fragment CollectionMosaicSuiteCategory on SanitySuiteCategory {
    _id
    id
    fullPath
    metadata {
      tracking {
        name
      }
    }
    card {
      ...AsCard
    }
  }
  fragment CollectionMosaicDiningAreaCategory on SanityDiningAreaCategory {
    _id
    id
    fullPath
    metadata {
      tracking {
        name
      }
    }
    card {
      ...AsCard
    }
  }

  fragment CollectionMosaicPublicAreaCategory on SanityPublicAreaCategory {
    _id
    id
    fullPath
    metadata {
      tracking {
        name
      }
    }
    card {
      ...AsCard
    }
  }

  fragment CollectionMosaicExclusiveOfferPerPromo on SanityExclusiveOfferPerPromo {
    ...OfferContent
    hideInCollections
  }

  fragment PageBuilderCollectionMosaic on SanityCollectionMosaic {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    collection
    collectionData {
      ... on SanityShip {
        ...CollectionMosaicShip
      }

      ... on SanityDestination {
        ...CollectionMosaicDestination
      }

      ... on SanitySpecialVoyage {
        ...CollectionMosaicSpecialVoyage
      }

      ... on SanitySuiteCategory {
        ...CollectionMosaicSuiteCategory
      }

      ... on SanityDiningAreaCategory {
        ...CollectionMosaicDiningAreaCategory
      }

      ... on SanityPublicAreaCategory {
        ...CollectionMosaicPublicAreaCategory
      }

      ... on SanityExclusiveOfferPerPromo {
        ...CollectionMosaicExclusiveOfferPerPromo
      }
    }
    filterByType
    mosaicVariant
  }
`;
